import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { DashboardUser, DashboardUserStatus, translateDashboardUserStatus } from '@/model';
import { formatDocument } from '@/utils/formatDocument';
import { t } from '@/i18n';

export interface DashboardUserTableProps {
  data: DashboardUser[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: DashboardUser) => void;
  onDeleteClick?: (item: DashboardUser) => void;
}

export const AccessControlUsersTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: DashboardUserTableProps) => {
  const columnHelper = createColumnHelper<{
    uuid: string;
    name: string;
    status: DashboardUserStatus;
    document: string;
    email: string;
    created_at: string;
    updated_at: string;
  }>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.access_control.components.users_table.column_created_at') || '',
    }),
    columnHelper.accessor('name', {
      cell: props => props.getValue(),
      header: t('pages.access_control.components.users_table.column_name') || '',
    }),
    columnHelper.accessor('document', {
      cell: props => formatDocument(props.getValue()),
      header: t('pages.access_control.components.users_table.column_document') || '',
    }),
    columnHelper.accessor('email', {
      cell: props => props.getValue(),
      header: t('pages.access_control.components.users_table.column_email') || '',
    }),
    columnHelper.accessor('updated_at', {
      cell: props => props.getValue(),
      header: t('pages.access_control.components.users_table.column_last_access') || '',
    }),
    columnHelper.accessor('status', {
      cell: props => translateDashboardUserStatus(props.getValue() as DashboardUserStatus),
      header: t('pages.access_control.components.users_table.column_status') || '',
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.access_control.components.users_table.column_actions') || '',

      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.access_control.components.users_table.aria_label_more_actions') || ''}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as DashboardUser)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text> {t('pages.access_control.components.users_table.action_view_details') || ''}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as DashboardUser)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text> {t('pages.access_control.components.users_table.action_delete') || ''}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
